import React, { Component } from 'react';
import { changeLocation } from 'api/Location';

import Auth from '../../routes/Auth';
import Intro from '../components/onboarding/Intro';
import PathChoice from '../components/onboarding/PathChoice';
import SimulationSelection from '../components/onboarding/SimulationSelection';
import TeamEvaluationSurvey from '../components/onboarding/TeamEvaluationSurvey';
import { fetchOnboardingSetup, getNextOnboardingStep } from '../components/onboarding/onboardingRouting';

// First-login onboarding for instructors. Triggered from
// start.js when user.onboard === 0 AND no game exists. The
// container dispatches on the :step route param to one of the
// four page components; unknown steps redirect back to intro.

const STEP_COMPONENTS = {
  'intro': Intro,
  'path-choice': PathChoice,
  'simulation-selection': SimulationSelection,
  'team-evaluation-survey': TeamEvaluationSurvey,
};

class InstructorOnboardingPage extends Component {
  state = {
    // null until fetchOnboardingSetup resolves. Prevents rendering a
    // step (or redirecting) before we know whether the current course
    // is missing a game or a survey.
    setup: null,
  };

  componentDidMount() {
    this.resolveEntry();
  }

  componentDidUpdate(prevProps) {
    const prevStep = prevProps.params && prevProps.params.step;
    const currStep = this.props.params && this.props.params.step;
    if (prevStep !== currStep) {
      this.resolveEntry();
    }
  }

  // Fetch { hasGame, hasSurvey } for the current course and decide:
  //  - Should this URL be skipped (already-satisfied step)?
  //  - Should this user even be here (already fully onboarded AND
  //    course has both a game and survey)?
  resolveEntry = () => {
    fetchOnboardingSetup()
      .then(setup => {
        if (this.checkRedirect(setup)) return;
        this.setState({ setup });
        this.normalize();
      })
      .catch(() => {
        // Best-effort: on failure just render the requested step so
        // the user isn't blocked.
        this.setState({ setup: { hasGame: false, hasSurvey: false } });
        this.normalize();
      });
  };

  // Users who have already completed onboarding AND whose course is
  // fully set up shouldn't re-enter the flow — send them to welcome.
  // Users still mid-flow (user.onboard === 0), OR onboarded users
  // whose course is missing a game/survey, ARE allowed in.
  checkRedirect = (setup) => {
    const user = Auth.getUser();
    const onboardComplete = !!(user && user.onboard !== 0);
    const needsSetup = !setup.hasGame || !setup.hasSurvey;
    const { params } = this.props;
    const currentStep = (params && params.step) || 'intro';

    console.log('[MG:onboarding] container.checkRedirect input:', {
      currentStep, onboardComplete, hasGame: setup.hasGame, hasSurvey: setup.hasSurvey, needsSetup,
    });

    if (onboardComplete && !needsSetup) {
      console.log('[MG:onboarding] container.checkRedirect -> /instructor/welcome (already onboarded, no setup needed)');
      changeLocation('/instructor/welcome');
      return true;
    }

    // If the current step should be skipped (e.g. user landed on
    // /simulation-selection but a game already exists), forward
    // them to the next needed step.
    const skipReason = (
      (currentStep === 'path-choice' && onboardComplete)
      || (currentStep === 'simulation-selection' && setup.hasGame)
      || (currentStep === 'team-evaluation-survey' && setup.hasSurvey)
    );
    if (skipReason) {
      const next = getNextOnboardingStep(currentStep, {
        hasGame: setup.hasGame,
        hasSurvey: setup.hasSurvey,
        onboardComplete,
      });
      console.log('[MG:onboarding] container.checkRedirect -> skipping', currentStep, 'to', next);
      changeLocation(next);
      return true;
    }
    console.log('[MG:onboarding] container.checkRedirect -> staying on', currentStep);
    return false;
  };

  normalize = () => {
    const { params } = this.props;
    const step = params && params.step;
    if (step && STEP_COMPONENTS[step]) return;
    if (!step) return;
    changeLocation('/instructor/onboarding/intro');
  };

  render() {
    const { setup } = this.state;
    if (!setup) return null;
    const { params } = this.props;
    const step = (params && params.step) || 'intro';
    const StepComponent = STEP_COMPONENTS[step] || Intro;
    return <StepComponent {...this.props} onboardingSetup={setup} />;
  }
}

export default InstructorOnboardingPage;



// WEBPACK FOOTER //
// ./src/instructor/containers/onboarding.js